import { useState, useEffect, useRef } from "react";
import { deleteMenu, get_All_Intent, getCompositeDataList, getMenu, InsertReservation, InsertSubIntent, saveKeyTopicOrder } from "../../Services/chatbot";
import { toast } from "react-toastify";
import { Fade, Modal } from "../../../evo/bootstrap";
import { OverlayTrigger, Tooltip } from "../../../evo/bootstrap";
import SortableItem from "../SortableItem/index";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import "../../assets/Menu.css"
import { sanitizeHtml, sanitizeCKEditorOnReady } from "../../Helper/sanitize";
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { ClassicEditor as ClassicEditorBase } from '../../Helper/ckeditor';
import { Accordion, Dropdown } from "../../../evo/bootstrap";
import DeleteModal from "../DeleteModal/index";
import OrderManageModal from "../OrderManageModal/index";
import { fixedMenuMaxOrder } from "../PropertyInfo/menuButtons";
import { nextTopicPosition, orderChanged, sortByTopicPosition, topicOrderPayload, topicPosition } from "./keyTopicOrder";
import { setISODay } from "date-fns";
import PublishModal from "../PublishModal/index";
import { CornerDownRight, ListTree, Pencil, Plus, Search, ArrowUpDown, Trash2, TriangleAlert } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/input-group";
import { htmlToText, richTextClass } from "@/components/admin/text";
import { EmptyState, Field, ModalFooter, ModalHeader, NativeSelect, PageHeader, SectionCard } from "@/components/admin/cards";


// Build full intent path for options value (with slashes)
const buildIntentId = (base, name) => `${base}/${name.toLowerCase().replace(/\s+/g, "_")}`;
// Build simple intentid (only last part, no slashes)
const buildIntentIdSimple = (name) => name.toLowerCase().replace(/\s+/g, "_");

const transformSingleMenuToFormData = (menu, propertyID, type = "composite") => {
  const fullIntentID = buildIntentIdSimple(menu.mainQuestion);
  const hasSub = menu.subQuestions && menu.subQuestions.length > 0;

  const options = hasSub
    ? menu.subQuestions.map((sq) => ({
      title: sq.question,
      value: buildIntentId(fullIntentID, sq.question),
    }))
    : [];

  const content: Record<string, any> = {};
  if (menu.mainAnswer) content.text = sanitizeHtml(menu.mainAnswer);
  if (options.length > 0) content.submenu = { options };

  return {
    // propertyID,
    intentID: fullIntentID,
    label: menu.mainQuestion,
    content,
    isParent: true,
    parentID: "",
    type: type,
    order_id: String(menu.order_id ?? 1),
    answerType: menu.answerType || 'text',
  };
};

const transformSingleSubMenuToFormData = (submenu, fullIntentPath, propertyID, type = "composite") => {
  const question = (submenu.question || '').trim();

  // The fullIntentPath is already correct
  const fullIntentID = fullIntentPath;

  const hasChildren = Array.isArray(submenu.subQuestions) && submenu.subQuestions.length > 0;

  const options = hasChildren
    ? submenu.subQuestions.map((child) => ({
      title: child.question,
      value: buildIntentId(fullIntentID, child.question),
    }))
    : [];

  const content: Record<string, any> = {};
  if (submenu.answer) content.text = sanitizeHtml(submenu.answer);
  if (options.length > 0) content.submenu = { options };

  return {
    // propertyID,
    intentID: fullIntentID,               // ✅ already full
    label: question,
    content,
    isParent: false,
    parentID: fullIntentPath
      .split('/')
      .slice(0, -1)
      .join('/'),                        // ✅ drop last segment to get parent path
    type: type,
    answerType: submenu.answerType || "text",
  };
};




// Button titles
// const Link = ({ id, children, title }) => (
//   <OverlayTrigger overlay={<Tooltip id={id}>{title}</Tooltip>}>
//     {children}
//   </OverlayTrigger>
// );

const Link = ({ id, children, title }: any) => (
  <OverlayTrigger overlay={<Tooltip id={id || 'tooltip'}>{title}</Tooltip>}>
    <span className="d-inline-block">{children}</span>
  </OverlayTrigger>
);


const IconAction = ({ label, onClick, destructive = false, children }: any) => (
  <Button type="button" variant="ghost" size="icon" title={label} onClick={onClick}
    className={cn("size-8", destructive && "text-destructive hover:bg-destructive/10 hover:text-destructive")}>
    {children}<span className="sr-only">{label}</span>
  </Button>
);

const renderSubQuestions = (
  subQuestions,
  parentId,
  updateSubQuestion,
  handleSubMenuDelete,
  addNestedSubQuestion,
  level = 2,
  prefix = '',
  setConfirmModal,
  setSubMenuContext,
  setShowSubMenuModal,
  labelQuestions,
  setSubmenuBackup
) => {
  return subQuestions.map((sq, index) => {
    const displayPrefix = `${prefix}.${index + 1}`;
    const isLastItem = index === subQuestions.length - 1;

    const addNested = () => {
      const newSub = {
        id: Date.now(),
        question: '',
        answer: '',
        answerType: 'editor',
        subQuestions: [],
      };

      addNestedSubQuestion(parentId, sq.id, newSub);

      setSubMenuContext({
        parentId,
        subId: newSub.id,
        isEdit: false
      });

      setShowSubMenuModal(true);
    };
    const childCount = sq.subQuestions?.length || 0;

    return (
      <li key={sq.id} className={isLastItem ? 'last-submenu-item' : undefined}>
        <div data-testid="sub-topic-row" className="group flex items-center gap-2 rounded-md py-1.5 pr-1 pl-2 transition-colors hover:bg-muted/60">
          <CornerDownRight className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
          <span className="min-w-0 flex-1 truncate text-sm" title={sq.question}>{sq.question}</span>
          {childCount > 0 && (
            <Badge variant="outline" className="text-muted-foreground">{childCount} sub-topic{childCount === 1 ? '' : 's'}</Badge>
          )}
          <div className="flex shrink-0 items-center gap-0.5 transition-opacity lg:opacity-60 lg:group-hover:opacity-100 lg:group-focus-within:opacity-100">
            {level < 3 && (
              <IconAction label="Add nested sub-topic" onClick={addNested}><Plus /></IconAction>
            )}
            <IconAction label="Edit" onClick={() => {
              setSubMenuContext({ parentId, subId: sq.id, isEdit: true });
              setSubmenuBackup(JSON.parse(JSON.stringify(labelQuestions)));
              setShowSubMenuModal(true);
            }}><Pencil /></IconAction>
            <IconAction label="Remove" destructive onClick={() => {
              setConfirmModal({
                show: true,
                label: `Sub-topic : "${sq.question}"`,
                onConfirm: () => handleSubMenuDelete(sq),
              });
            }}><Trash2 /></IconAction>
          </div>
        </div>

        {/* Recursive rendering for nested subquestions */}
        {sq.subQuestions?.length > 0 && (
          <ul className="ml-3.5 flex flex-col border-l border-border pl-2">
            {renderSubQuestions(
              sq.subQuestions,
              parentId,
              updateSubQuestion,
              handleSubMenuDelete,
              addNestedSubQuestion,
              level + 1,
              displayPrefix,
              setConfirmModal,
              setSubMenuContext,
              setShowSubMenuModal,
              labelQuestions,
              setSubmenuBackup
            )}
          </ul>
        )}
      </li>
    );
  });
};


const IntentSection = ({ setPublishTrigger, setPublishModal, setIsNeedPublish, propertyID, setIsDirty, shouldLoadIntent, onLoaded, isDirty, menuRecords, onTopicsChanged }: any) => {
  const isContactType = (type) => type === "contact_us";
  
  // Helper function to sanitize title by removing / and \ characters
  const sanitizeTitle = (value) => {
    return value.replace(/[/\\]/g, '');
  };

  const [showSubMenuModal, setShowSubMenuModal] = useState(false);
  const [subMenuContext, setSubMenuContext] = useState<any>({
    parentId: null,
    subId: null,
    isEdit: false
  });
  const [labelQuestions, setLabelQuestions] = useState<any>([]);
  const [menuForm, setMenuForm] = useState<any>({
    title: '',
    answer: '',
    answerType: 'text'
  });

  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState<any>(null);
  const [visibleCount, setVisibleCount] = useState(5);
  const [deleteName, setDeleteName] = useState('');
  const [openAccordionId, setOpenAccordionId] = useState<any>(null);
  const [viewTopicId, setViewTopicId] = useState<any>(null);
  const [confirmModal, setConfirmModal] = useState<any>({
    show: false,
    id: null,
    label: '',
    onConfirm: null,
  });
  const [newlyAddedMenuId, setNewlyAddedMenuId] = useState<any>(null);
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingMenuId, setEditingMenuId] = useState<any>(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [orderDraft, setOrderDraft] = useState<any>([]);
  const [orderSaving, setOrderSaving] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [reservationIntent, setReservationIntent] = useState<any>(null);
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [reservationTitle, setReservationTitle] = useState("");
  const [data, setData] = useState<any>([]);
  const [submenuBackup, setSubmenuBackup] = useState<any>(null);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [topicSearch, setTopicSearch] = useState("");
  let closeAction = null;
  const [formErrors, setFormErrors] = useState<any>({
    title: ""
  })
  const [invalidCharMessage, setInvalidCharMessage] = useState<any>({
    show: false,
    message: ""
  })

  useEffect(() => {
    if (!isDirty) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);




  useEffect(() => {
    if (shouldLoadIntent) {
      getAllIntentData();
      onLoaded();  // ✅ reset the trigger after loading
    }
  }, [shouldLoadIntent]);

  const getAllIntentData = async () => {
    setLoading(true)
    try {
      const res = await get_All_Intent();
      if (res?.status === 200 && res?.data) {
        setData(res.data);
      } else {
        setData([]);
      }
    } catch (error) {
      setData([]);

      // Safely check error response (especially when using Axios)
      if (error?.response?.status === 401) {
        toast.error("Access denied. Please verify using a valid link.");
      } else {
        console.error("Failed to load FAQ data:", error);
       // toast.error("Failed to load data. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }


  const showMoreMenus = () => {
    setVisibleCount(labelQuestions.length);
  };

  const convertAPIDataToLabelQuestions = (apiData) => {

    //console.log("api data", apiData)
    const intentMap: Record<string, any> = {};
    const labelQuestions = [];

    // Step 1: Store all intents by their intentID
    apiData.forEach((item) => {

      intentMap[item.intentID] = {
        ...item,
        id: item.intentID,
        intentID: item.intentID,
        question: item.label,
        answer: sanitizeHtml(item.content.text || ''),
        answerType: item.answerType || 'text',
        subQuestions: [],
      };
    });

    // Step 2: Attach children to their parents
    Object.values(intentMap).forEach((intent) => {
      if (!intent.isParent) {
        const parent = intentMap[intent.parentID];
        if (parent) {
          parent.subQuestions.push(intent);
        }
      }
    });

    // Step 3: Gather all top-level parents as labelQuestions
    Object.values(intentMap).forEach((intent) => {


      if (intent.isParent) {
        labelQuestions.push({
          id: intent.id,
          intentID: intent.intentID,
          mainQuestion: intent.label,
          mainAnswer: sanitizeHtml(intent.content?.text || ''),
          subQuestions: intent.subQuestions,
          order_id: topicPosition(intent),
          answerType: intent.answerType || 'text',
        });

      }


    });

    // Widget order: first in the list = first menu button (smallest position).
    return sortByTopicPosition(labelQuestions);
  };



  // Always include intentID optionally
  const loadUpdatedList = async (intentToOpen = null) => {
    const input = { type: "Composite" };
    try {
      const res = await getCompositeDataList(input);
      if (res.status === 200) {
        const formatted = convertAPIDataToLabelQuestions(res.data);
        setLabelQuestions(formatted);
        onTopicsChanged?.();

        // ✅ Only set openAccordionId if intentToOpen is provided
        if (intentToOpen) {
          const matched = formatted.find(q => q.intentID === intentToOpen);
          if (matched) {
            setOpenAccordionId(matched.id);
          } else {
            setOpenAccordionId(null);
          }
        }
        // else - keep accordion state as is
      } else {
        toast.error("Error fetching data");
      }
    } catch (error) {
      if (error.status === 401) {
        toast.error("Access denied. Please verify using a valid link.")
      }
      console.error("Failed to load config:", error);
      // toast.error(error.message || "Something went wrong")
      // toast.error("Failed to load updated list");
      //console.error(err);
    }
  };


  useEffect(() => {

    if (data?.length > 0) {
      const formatted = convertAPIDataToLabelQuestions(data);
      setLabelQuestions(formatted); // Don't reverse

      setIsEditMode(true);
    }
  }, [data]);

  const addLabelQuestion = () => {
    setEditingMenuId(null);
    setMenuForm({
      title: '',
      answer: '',
      answerType: 'editor'
    });
    setShowAddModal(true);
  };


  const updateSubQuestion = (parentId, subId, field, value) => {
    const recursiveUpdate = (subs) =>
      subs.map((sq) => {
        if (sq.id === subId) {
          return { ...sq, [field]: value };
        }
        if (sq.subQuestions && sq.subQuestions.length > 0) {
          return {
            ...sq,
            subQuestions: recursiveUpdate(sq.subQuestions),
          };
        }
        return sq;
      });

    setLabelQuestions((prev) =>
      prev.map((q) => ({
        ...q,
        subQuestions: recursiveUpdate(q.subQuestions),
      }))
    );
  };


  const collectAllDescendantIntentIDs = (node) => {
    const collected = [];

    const traverse = (subNode) => {
      if (!subNode || !subNode.subQuestions) return;
      for (const child of subNode.subQuestions) {
        collected.push(child.intentID);
        traverse(child); // recursive
      }
    };

    traverse(node);
    return collected;
  };


  const handleSubMenuDelete = async (sub) => {
    setLoading(true)
    const subintentIDs = collectAllDescendantIntentIDs(sub);

    // Find parentID
    const findParentIntentID = (nodes, targetId) => {
      for (const node of nodes) {
        if (node.subQuestions?.some(sq => sq.id === targetId)) {
          return node.intentID;
        }
        const deep = findParentIntentID(node.subQuestions || [], targetId);
        if (deep) return deep;
      }
      return '';
    };

    const parentID = findParentIntentID(labelQuestions, sub.id);

    const payload = {
      // propertyID,
      intentID: sub.intentID,
      isParent: false,
      parentid: parentID,
      subintentID: subintentIDs,
      type: isContactType ? "contact_us" : "composite"
    };

    // console.log("🚮 Submenu Delete Payload:", payload);

    try {
      const res = await deleteMenu(payload);
      if (res.status === 200) {
        toast.success("Submenu deleted successfully");

        // ✅ ✅ ✅ Update labelQuestions in UI:
        const removeSubById = (nodes, targetId) => {
          return nodes
            .map(node => ({
              ...node,
              subQuestions: node.subQuestions
                ? removeSubById(node.subQuestions, targetId)
                : [],
            }))
            .filter(node => node.id !== targetId);
        };

        const updated = removeSubById(labelQuestions, sub.id);
        setLabelQuestions(updated);
        setIsDirty(false)
        setLoading(false)
        setIsNeedPublish(true)
        setPublishTrigger(prev => prev + 1);
      } else {
        toast.error("Failed to delete submenu");
      }
    } catch (error) {
      if (error.status === 401) {
        toast.error("Access denied. Please verify using a valid link.")
      }
      console.error("Failed to load config:", error);
      toast.error(error.message || "Something went wrong")
    } finally {
      setLoading(false)
    }
  };




  const addNestedSubQuestion = (parentId, subId, newSub = null) => {
    const recursiveAdd = (subs) => subs.map(sq => {
      if (sq.id === subId) {
        return {
          ...sq,
          subQuestions: [
            ...(sq.subQuestions || []),
            newSub || { id: Date.now(), question: '', answer: '', answerType: 'text', subQuestions: [] }
          ]
        };
      }
      return {
        ...sq,
        subQuestions: recursiveAdd(sq.subQuestions || [])
      };
    });

    setLabelQuestions(prev =>
      prev.map(q => {
        if (q.id === parentId) return { ...q, subQuestions: recursiveAdd(q.subQuestions) };
        return q;
      })
    );
  };


  const collectAllSubIntentIDs = (menu) => {
    const ids = [];

    const traverse = (node) => {
      if (!node?.subQuestions || node.subQuestions.length === 0) return;

      for (const child of node.subQuestions) {
        ids.push(child.intentID);
        traverse(child);
      }
    };

    traverse(menu);
    return ids;
  };

  const handleDeleteConfirmed = async (deleteId) => {
    setLoading(true)
    const menuToDelete = labelQuestions.find(q => q.id === deleteId);
    if (!menuToDelete) {
      toast.error("Menu not found");
      return;
    }

    const subintentIDs = collectAllSubIntentIDs(menuToDelete);

    const payload = {
      // propertyID,
      intentID: menuToDelete.intentID,
      isParent: true,
      subintentID: subintentIDs,
    };

    // console.log("🗑️ Final delete payload:", payload);

    try {
      const res = await deleteMenu(payload);
      if (res.status === 200) {
        toast.success("Menu deleted successfully");

        // ✅ Remove the menu from UI
        const updated = labelQuestions.filter(q => q.id !== deleteId);
        setLabelQuestions(updated);
        setIsDirty(false)
        setDeleteId(null);
        setLoading(false);
        setIsNeedPublish(true)
        setPublishTrigger(prev => prev + 1);

      } else {
        toast.error("Failed to delete menu");
      }
    } catch (error) {
      if (error.status === 401) {
        toast.error("Access denied. Please verify using a valid link.")
      }
      console.error("Failed to load config:", error);
      toast.error(error.message || "Something went wrong")
    } finally {
      setLoading(false)
    }
  };


  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = orderDraft.findIndex(q => q.id === active.id);
      const newIndex = orderDraft.findIndex(q => q.id === over.id);
      const moved = arrayMove<any>(orderDraft, oldIndex, newIndex);
      setOrderDraft(moved);
      setIsDirty(orderChanged(moved, labelQuestions));
    }
  };

  const openOrderModal = () => {
    setOrderDraft(labelQuestions);
    setShowOrderModal(true);
  };

  const discardOrder = () => {
    setOrderDraft([]);
    setIsDirty(false);
    setShowOrderModal(false);
  };

  const closeOrderModal = () => {
    if (orderSaving) return;
    if (!orderChanged(orderDraft, labelQuestions)) {
      discardOrder();
      return;
    }
    setPublishModal({
      show: true,
      title: "Unsaved Changes",
      label: "You have unsaved changes. Do you want to leave without saving?",
      confirmText: "Yes, Leave",
      cancelText: "Stay",
      onConfirm: () => {
        discardOrder();
        setPublishModal(prev => ({ ...prev, show: false }));
      },
      onClose: () => {
        setPublishModal(prev => ({ ...prev, show: false }));
      }
    });
  };

  // Saves the whole key topic order in one call; the Lambda stores positions after the fixed buttons.
  const saveSortedOrder = async () => {
    setOrderSaving(true);
    try {
      const res = await saveKeyTopicOrder({ topics: topicOrderPayload(orderDraft) });
      if (res.status === 200) {
        toast.success("Key topic order saved");
        setIsDirty(false);
        setShowOrderModal(false);
        setOrderDraft([]);
        await loadUpdatedList();
        setIsNeedPublish(true);
        setPublishTrigger(prev => prev + 1);
      } else {
        toast.error(res.data?.error || "Could not save the key topic order");
      }
    } catch (error) {
      if (error.status === 401) {
        toast.error("Access denied. Please verify using a valid link.")
      } else {
        toast.error(error.response?.data?.error || error.message || "Could not save the key topic order");
      }
      console.error("Failed to save key topic order:", error);
    } finally {
      setOrderSaving(false);
    }
  };

  const findSubQuestionById = (subQuestions, id) => {
    for (const sq of subQuestions) {
      if (sq.id === id) return sq;
      if (sq.subQuestions?.length) {
        const found = findSubQuestionById(sq.subQuestions, id);
        if (found) return found;
      }
    }
    return null;
  };

  const saveSingleMenu = async () => {

    if (!menuForm.title.trim()) {
      //toast.error("Menu title is required");
      setFormErrors({
        title: "topic name is required !"
      })
      return;
    }

    // Validate that title doesn't contain / or \ characters
    if (menuForm.title.includes('/') || menuForm.title.includes('\\')) {
      setFormErrors({
        title: "Topic name cannot contain '/' or '\\' characters"
      })
      return;
    }

    let updatedLabelQuestions;

    if (editingMenuId === null) {
      setModalLoading(true)
      // console.log("Add new mwnu called")
      // ------------------------
      // 🟢 ADD NEW MENU
      // ------------------------
      const fullIntentID = buildIntentIdSimple(menuForm.title);
      const newMenu = {
        id: Date.now(),
        mainQuestion: menuForm.title,
        mainAnswer: menuForm.answer,
        answerType: menuForm.answerType,
        subQuestions: [],
        order_id: nextTopicPosition(labelQuestions, fixedMenuMaxOrder(menuRecords || {})),
        intentID: fullIntentID
      };

      const payload = {
        ...transformSingleMenuToFormData(newMenu, propertyID),
        isModify: "Y",
        type: "composite"
      };

      try {
        const res = await InsertSubIntent(payload);
        if (res.status === 200) {
          toast.success("Menu saved successfully");
          //  updatedLabelQuestions = [newMenu, ...labelQuestions];
          await loadUpdatedList(fullIntentID);
          setIsDirty(false)
          setModalLoading(false)
          setIsNeedPublish(true)
          setPublishTrigger(prev => prev + 1);
        } else {
          toast.error(res.data?.message || "Something went wrong!");
          return;
        }
      } catch (error) {
        if (error.status === 401) {
          toast.error("Access denied. Please verify using a valid link.")
        }
        console.error("Failed to load config:", error);
        toast.error(error.message || "Something went wrong")
        return;
      } finally {
        setModalLoading(false)
      }

    } else {


      // ------------------------
      // ✏️ EDIT EXISTING MENU
      // ------------------------
      setModalLoading(true)
      const menuToEdit = labelQuestions.find((q) => q.id === editingMenuId);
      if (!menuToEdit) {
        toast.error("Menu not found for editing");
        return;
      }

      const oldIntentID = buildIntentIdSimple(menuToEdit.mainQuestion);
      const newIntentID = buildIntentIdSimple(menuForm.title);

      const updatedMenu = {
        ...menuToEdit,
        mainQuestion: menuForm.title,
        mainAnswer: menuForm.answer,
        answerType: menuForm.answerType,
      };

      const updatedPayload = transformSingleMenuToFormData(updatedMenu, propertyID);

      // 🧠 Recursively collect submenu and subsubmenu mappings
      const collectNestedSubmenuMappings = (questions, oldParentPath, newParentPath) => {
        return (questions || []).map((sq) => {
          const title = sq.question?.trim();
          if (!title) return null;

          const oldId = buildIntentId(oldParentPath, title);
          const newId = buildIntentId(newParentPath, title);

          const mapping: Record<string, any> = {
            oldId,
            newId,
            newparentID: newParentPath,
          };

          if (sq.subQuestions?.length > 0) {
            mapping.submenu = collectNestedSubmenuMappings(sq.subQuestions, oldId, newId);
          }

          return mapping;
        }).filter(Boolean);
      };

      const submenuToUpdate = collectNestedSubmenuMappings(
        menuToEdit.subQuestions || [],
        oldIntentID,
        newIntentID
      );

      const payloadForEdit = {
        // propertyID,
        oldintentID: oldIntentID,
        submenuToUpdate,
        item: {
          ...updatedPayload,
          type: menuToEdit.type === "contact_us" ? "contact_us" : "composite"
        },
        isModify: "Y"
      };

      //console.log("Final Edit Payload:", payloadForEdit);

      try {
        const res = await InsertSubIntent(payloadForEdit);
        if (res.status === 200) {
          toast.success("Menu updated successfully");
          // updatedLabelQuestions = labelQuestions.map((q) =>
          //   q.id === editingMenuId ? updatedMenu : q
          // );
          setModalLoading(false)
          setIsDirty(false)
          setIsNeedPublish(true)
          setPublishTrigger(prev => prev + 1);
          await loadUpdatedList(newIntentID);
        } else {
          toast.error(res.data?.message || "Something went wrong!");
          return;
        }
      } catch (error) {
        if (error.status === 401) {
          toast.error("Access denied. Please verify using a valid link.")
        }
        console.error("Failed to load config:", error);
        toast.error(error.message || "Something went wrong")
        return;
      } finally {
        setModalLoading(false)
      }
    }

    // setLabelQuestions(updatedLabelQuestions);
    setEditingMenuId(null);
    setShowAddModal(false);
  }



  const findFullIntentPathforadd = (parent, subId) => {
    const path = [];

    const dfs = (subs, trail) => {
      for (const sq of subs) {
        const newTrail = [...trail, sq.question];
        if (sq.id === subId) {
          path.push(...newTrail);
          return true;
        }
        if (sq.subQuestions?.length && dfs(sq.subQuestions, newTrail)) {
          return true;
        }
      }
      return false;
    };

    dfs(parent.subQuestions, [parent.mainQuestion]);
    return path.length > 0 ? path.join('/').toLowerCase().replace(/\s+/g, '_') : '';
  };

  function findParentOfSub(list, subId) {
    for (const item of list) {
      if (item.subQuestions?.some(sq => sq.id === subId)) {
        return item;
      }
      if (item.subQuestions?.length) {
        const deep = findParentOfSub(item.subQuestions, subId);
        if (deep) return deep;
      }
    }
    return null;
  }


  const handleSubMenuAdd = async () => {

    const parent = labelQuestions.find(q => q.id === subMenuContext.parentId);
    const sub = findSubQuestionByIdFOrAdd(parent.subQuestions, subMenuContext.subId);

    if (!sub.question?.trim()) {
      //toast.error("Menu title is required");
      setFormErrors({
        title: "topic name is required !"
      })
      return;
    }

    // Validate that question doesn't contain / or \ characters
    if (sub.question.includes('/') || sub.question.includes('\\')) {
      setFormErrors({
        title: "Sub-topic name cannot contain '/' or '\\' characters"
      })
      return;
    }
    setModalLoading(true)
    const fullIntentPath = findFullIntentPathforadd(parent, subMenuContext.subId);
    const payload = {
      ...transformSingleSubMenuToFormData(sub, fullIntentPath, propertyID),
      isModify: "Y"
    };

    //  console.log("payload for submenu", payload)
    try {
      const res = await InsertSubIntent(payload);
      if (res.status === 200) {
        toast.success("Submenu saved successfully");
        setShowSubMenuModal(false);
        setIsDirty(false)
        setIsNeedPublish(true)
        setPublishTrigger(prev => prev + 1);
        setSubmenuBackup(null);
        await loadUpdatedList();
        setModalLoading(false)
      } else {
        toast.error(res.data?.message || "Failed to save submenu");
      }
    } catch (error) {
      if (error.status === 401) {
        toast.error("Access denied. Please verify using a valid link.")
      }
      console.error("Failed to load config:", error);
      toast.error(error.message || "Something went wrong")
    } finally {
      setModalLoading(false)
      // setSubmenuBackup(null);

    }
  };

  const handleSubMenuUpdate = async () => {

    const sub = findSubQuestionByIdInTree(labelQuestions, subMenuContext.subId);
    const parent = findParentOfSub(labelQuestions, subMenuContext.subId);

    //console.log("🔍 Actual Parent:", parent);
    //console.log("🔧 Submenu Being Saved:", sub);

    // if (!sub || !sub.question?.trim()) {
    //   toast.error("Submenu question is required");
    //   return;
    // }
    if (!sub.question?.trim()) {
      //toast.error("Menu title is required");
      setFormErrors({
        title: "topic name is required !"
      })
      return;
    }

    // Validate that question doesn't contain / or \ characters
    if (sub.question.includes('/') || sub.question.includes('\\')) {
      setFormErrors({
        title: "Sub-topic name cannot contain '/' or '\\' characters"
      })
      return;
    }
    setModalLoading(true)
    const newIntentID = buildIntentId(parent.intentID, sub.question.trim());
    const oldsubintentID = buildIntentId(
      parent.intentID,
      sub.originalLabel || sub.question
    );

    const item = transformSingleSubMenuToFormData(sub, newIntentID, propertyID);

    const oldIntentID = buildIntentId(
      parent.intentID,
      sub.originalLabel || sub.question
    );

    const submenuToUpdate = (sub.subQuestions || []).map((child) => {
      const oldId = buildIntentId(
        oldIntentID,
        child.originalLabel || child.question
      );
      const newId = buildIntentId(newIntentID, child.question);
      return {
        oldId,
        newId,
        newparentID: newIntentID,
      };
    });


    const payload = {
      // propertyID,
      oldsubintentID,
      newsubintentID: newIntentID,
      parentID: parent.intentID,
      item,
      submenuToUpdate,
      isModify: "Y",
      titleToUpdate: item.label
    };



    try {
      const res = await InsertSubIntent(payload);
      if (res.status === 200) {
        toast.success("Submenu saved successfully");
        setShowSubMenuModal(false);
        setIsNeedPublish(true)
        setPublishTrigger(prev => prev + 1);
        setSubmenuBackup(null);
        await loadUpdatedList();
        setIsDirty(false)
        setModalLoading(false)
      } else {
        toast.error(res.data?.message || "Failed to save submenu");
      }
    } catch (error) {
      if (error.status === 401) {
        toast.error("Access denied. Please verify using a valid link.")
      }
      console.error("Failed to load config:", error);
      toast.error(error.message || "Something went wrong")
    } finally {
      setModalLoading(false)
    }
  }


  const findIntentPathBySubId = (questions, targetSubId, path = []) => {
    for (const q of questions) {
      const currentPath = [...path, q];
      if (q.id === targetSubId) return currentPath;
      if (q.subQuestions?.length) {
        const found = findIntentPathBySubId(q.subQuestions, targetSubId, currentPath);
        if (found) return found;
      }
    }
    return null;
  };


  const findSubQuestionByIdInTree = (questions, id) => {
    for (const q of questions) {
      if (q.id === id) return q;
      if (q.subQuestions?.length) {
        const found = findSubQuestionByIdInTree(q.subQuestions, id);
        if (found) return found;
      }
    }
    return null;
  };

  const findSubQuestionByIdFOrAdd = (subQuestions, id) => {
    for (const sq of subQuestions) {
      if (sq.id === id) return sq;
      if (sq.subQuestions?.length) {
        const found = findSubQuestionById(sq.subQuestions, id);
        if (found) return found;
      }
    }
    return null;
  }


  const buildSubQuestionsRecursive = (options = [], parentIntentId, allDataMap) => {
    return options.map(opt => {
      const fullIntentId = opt.value;
      const subIntentData = allDataMap[fullIntentId];

      const nestedOptions = subIntentData?.content?.submenu?.options || [];

      return {
        id: Date.now() + Math.random(),
        question: opt.title || '',
        answer: subIntentData?.content?.text || '',
        answerType: subIntentData?.answerType || 'text',
        subQuestions: buildSubQuestionsRecursive(nestedOptions, fullIntentId, allDataMap),
      };
    });
  };

  const handleMenuEdit = async (intentID) => {
    //console.log("handle menu")
    setLoading(true);

    // ✅ First, check contactLabelQuestions (contact_us type)


    // ✅ Else, handle normal composite menu
    const input = { intentID };
    const res = await getMenu(input);

    if (res?.data) {
      const { label, content, answerType, intentID: intent } = res.data;
      const menuId = Date.now();

      // ✅ Find the matching composite menu in labelQuestions
      const original = labelQuestions.find((q) => q.intentID === intent);

      let subQuestions = [];

      if (original) {
        subQuestions = JSON.parse(JSON.stringify(original.subQuestions || []));
      } else {
        // fallback if no local structure
        subQuestions = (content?.submenu?.options || []).map((opt) => ({
          id: Date.now() + Math.random(),
          question: opt.title || '',
          originalLabel: opt.title || '',
          answer: '',
          answerType: 'text',
          subQuestions: [],
        }));
      }

      const menu = {
        id: menuId,
        intentID: intent,
        mainQuestion: label || '',
        mainAnswer: content?.text || '',
        answerType: answerType || 'text',
        subQuestions,
        order_id: topicPosition(res.data) || 1,
        type: "composite"
      };

      setMenuForm({
        title: menu.mainQuestion,
        answer: menu.mainAnswer,
        answerType: menu.answerType,
      });

      // ✅ Update labelQuestions only for composite
      setLabelQuestions((prev) => {
        const withoutOld = prev.filter((q) => q.intentID !== intent);
        return [...withoutOld, { ...menu }];
      });

      setEditingMenuId(menuId);
      setShowAddModal(true);
    }

    setLoading(false);
  };

  // const clossubmenu = () => {
  //   if (isDirty) {
  //     closeAction = () => {
  //       if (submenuBackup) {
  //         setLabelQuestions(submenuBackup);
  //         setSubmenuBackup(null);
  //       }
  //       setIsDirty(false);
  //       setSubMenuContext({ parentId: null, subId: null, isEdit: false });
  //       setShowSubMenuModal(false); // ✅ closes submenu modal
  //     };

  //     setPublishModal({
  //       show: true,
  //       title: "Unsaved Changes",
  //       label: "You have unsaved changes. Do you want to leave without saving?",
  //       confirmText: "Yes, Leave",
  //       cancelText: "Stay",
  //       onConfirm: () => {
  //         if (closeAction) closeAction(); // ✅ also closes submenu modal
  //         setPublishModal(prev => ({ ...prev, show: false }));
  //       },
  //       onClose: () => {
  //         // ❌ Stay on current tab
  //         setPublishModal(prev => ({ ...prev, show: false }));
  //       }
  //     });
  //     return;
  //   }

  //   if (!subMenuContext.isEdit && subMenuContext.subId && subMenuContext.parentId) {
  //     const removeSubById = (subQuestions) =>
  //       subQuestions
  //         .filter(sub => sub.id !== subMenuContext.subId)
  //         .map(sub => ({
  //           ...sub,
  //           subQuestions: removeSubById(sub.subQuestions || []),
  //         }));

  //     setLabelQuestions(prev =>
  //       prev.map(q => ({
  //         ...q,
  //         subQuestions: removeSubById(q.subQuestions || [])
  //       }))
  //     );
  //   }

  //   if (submenuBackup) {
  //     setLabelQuestions(submenuBackup);
  //     setSubmenuBackup(null);
  //   }
  //   setIsDirty(false);
  //   setSubMenuContext({ parentId: null, subId: null, isEdit: false });
  //   setShowSubMenuModal(false);
  // };

  const clossubmenu = () => {
    setFormErrors(prev => ({ ...prev, title: "" }));
    setInvalidCharMessage({ show: false, message: "" });
    const removeSubByIdRecursive = (subs, targetId) => {
      return subs
        .filter(sub => sub.id !== targetId) // remove the target
        .map(sub => ({
          ...sub,
          subQuestions: removeSubByIdRecursive(sub.subQuestions || [], targetId) // recurse into children
        }));
    };

    if (isDirty) {
      closeAction = () => {
        if (!subMenuContext.isEdit && subMenuContext.subId && subMenuContext.parentId) {
          // Recursively remove newly added unsaved submenu anywhere in tree
          setLabelQuestions(prev =>
            prev.map(q => ({
              ...q,
              subQuestions: removeSubByIdRecursive(q.subQuestions, subMenuContext.subId)
            }))
          );
        } else if (submenuBackup) {
          // Restore only when editing
          setLabelQuestions(submenuBackup);
        }

        setSubmenuBackup(null);
        setIsDirty(false);
        setSubMenuContext({ parentId: null, subId: null, isEdit: false });
        setShowSubMenuModal(false);
      };

      setPublishModal({
        show: true,
        title: "Unsaved Changes",
        label: "You have unsaved changes. Do you want to leave without saving?",
        confirmText: "Yes, Leave",
        cancelText: "Stay",
        onConfirm: () => {
          if (closeAction) closeAction();
          setPublishModal(prev => ({ ...prev, show: false }));
        },
        onClose: () => {
          setPublishModal(prev => ({ ...prev, show: false }));
        }
      });
      return;
    }

    // No unsaved changes: just remove if adding
    if (!subMenuContext.isEdit && subMenuContext.subId && subMenuContext.parentId) {
      setLabelQuestions(prev =>
        prev.map(q => ({
          ...q,
          subQuestions: removeSubByIdRecursive(q.subQuestions, subMenuContext.subId)
        }))
      );
    } else if (submenuBackup) {
      setLabelQuestions(submenuBackup);
      setSubmenuBackup(null);
    }

    setIsDirty(false);
    setSubMenuContext({ parentId: null, subId: null, isEdit: false });
    setShowSubMenuModal(false);
  };


  const handleMenuClose = () => {
    setFormErrors(prev => ({ ...prev, title: "" }));
    setInvalidCharMessage({ show: false, message: "" });
    // 2️⃣ Only remove blank menu if it's newly added and not in edit mode
    if (newlyAddedMenuId && !editingMenuId) {
      const isEmptyNewMenu = labelQuestions.some(
        q => q.id === newlyAddedMenuId &&
          q.mainQuestion.trim() === '' &&
          q.mainAnswer.trim() === ''
      );

      if (isEmptyNewMenu) {
        setLabelQuestions(prev =>
          prev.filter(q => q.id !== newlyAddedMenuId)
        );
      }
    }

    // 3️⃣ Final cleanup
    setIsDirty(false);
    setNewlyAddedMenuId(null);
    setEditingMenuId(null);
    setShowAddModal(false);
  };




  const requestMenuClose = () => {
    if (isDirty) {
      setPublishModal({
        show: true,
        title: "Unsaved Changes",
        label: "You have unsaved changes. Do you want to leave without saving?",
        confirmText: "Yes, Leave",
        cancelText: "Stay",
        onConfirm: () => {
          handleMenuClose();
          setPublishModal(prev => ({ ...prev, show: false }));
        },
        onClose: () => {
          setPublishModal(prev => ({ ...prev, show: false }));
        }
      });
    } else {
      // ✅ No changes — just close and clean up blank new menu
      handleMenuClose();
    }
  };

  const searchTerm = topicSearch.trim().toLowerCase();
  const filteredTopics = Array.isArray(labelQuestions)
    ? labelQuestions.filter(q => !searchTerm || (q.mainQuestion || '').toLowerCase().includes(searchTerm))
    : [];
  const viewTopic = labelQuestions.find(q => q.id === viewTopicId);
  const closeTopicView = () => setViewTopicId(null);

  return (
    <>
      <div className="d-flex flex-column gap-4">
      <PageHeader id="keyTopicsHeading" icon={<ListTree />} title="Key Topics"
        description="Menu buttons guests can tap in the chatbot, each with its own answer." />
      <div className="position-relative">
        {loading && (
          <div className="loaderOverlay zIndex-1023"><div className="loading"><div className="main-loader"></div></div></div>
        )}
        <section data-evo-island aria-labelledby="keyTopicsHeading"
          className={cn("text-foreground", loading && "pointer-events-none blur-sm")}>
          <SectionCard
            icon={<ListTree />}
            title={<>Your key topics <Badge variant="secondary" aria-label={`${labelQuestions.length} key topics`}>{labelQuestions.length}</Badge></>}
            description="Shown in this order after the fixed menu buttons. Click a topic to see its sub-topics."
            actions={
              <>
                <Button type="button" variant="outline" onClick={openOrderModal} disabled={labelQuestions.length < 2}>
                  <ArrowUpDown aria-hidden="true" />Manage order
                </Button>
                <Button type="button" onClick={addLabelQuestion}>
                  <Plus aria-hidden="true" />Add topic
                </Button>
              </>
            }
          >
            {labelQuestions.length > 0 && (
              <InputGroup className="sm:max-w-sm">
                <InputGroupAddon><Search aria-hidden="true" /></InputGroupAddon>
                <InputGroupInput type="search" aria-label="Search topics" placeholder="Search topics..."
                  value={topicSearch} onChange={(e) => setTopicSearch(e.target.value)} />
              </InputGroup>
            )}

            {filteredTopics.length === 0 && !loading && (
              searchTerm ? (
                <EmptyState icon={<Search />} title="No matching topics"
                  description={`No topic name contains "${topicSearch.trim()}". Try another word.`} />
              ) : (
                <EmptyState icon={<ListTree />} title="No key topics yet"
                  description="Click Add topic to create the first menu button guests can tap." />
              )
            )}

            {filteredTopics.length > 0 && (
              <ul className="question-div grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3" aria-label="Key topics">
                {filteredTopics.slice(0, visibleCount).map((lq) => {
                  const subCount = lq.subQuestions?.length || 0;
                  const preview = subCount > 0
                    ? lq.subQuestions.map(s => s.question).filter(Boolean).join(" · ")
                    : htmlToText(lq.mainAnswer);

                  return (
                    <li key={lq.id} data-testid="key-topic"
                      className="group flex min-w-0 cursor-pointer flex-col gap-2 rounded-xl border border-border bg-card p-4 text-card-foreground shadow-xs transition-all hover:border-primary/40 hover:shadow-md focus-within:border-primary/60"
                      onClick={(e) => {
                        e.stopPropagation();
                        setViewTopicId(lq.id);
                      }}>
                      <div className="flex items-start justify-between gap-2">
                        <button type="button" data-testid="key-topic-title" className="min-w-0 flex-1 pt-1.5 text-left text-sm font-semibold leading-5 break-words focus-visible:underline focus-visible:outline-none">{lq.mainQuestion}</button>
                        <div className="flex shrink-0 items-center gap-0.5">
                          <IconAction label="Add sub-topic" onClick={(e) => {
                            e.stopPropagation();
                            (() => {
                              const newSub = {
                                id: Date.now(),
                                question: '',
                                answer: '',
                                answerType: 'editor',
                                subQuestions: [],
                              };

                              // Inject new sub into parent
                              setLabelQuestions(prev =>
                                prev.map(q => {
                                  if (q.id === lq.id) {
                                    return { ...q, subQuestions: [...q.subQuestions, newSub] };
                                  }
                                  return q;
                                })
                              );

                              setSubMenuContext({
                                parentId: lq.id,
                                subId: newSub.id,
                                isEdit: false
                              });

                              setShowSubMenuModal(true);
                            })();
                          }}><Plus /></IconAction>
                          <IconAction label="Edit" onClick={(e) => {
                            e.stopPropagation();
                            handleMenuEdit(lq.intentID);
                          }}><Pencil /></IconAction>
                          <IconAction label="Remove" destructive onClick={(e) => {
                            e.stopPropagation();
                            setConfirmModal({
                              show: true,
                              label: `Delete topic : "${lq.mainQuestion}"`,
                              onConfirm: () => handleDeleteConfirmed(lq.id),
                            });
                          }}><Trash2 /></IconAction>
                        </div>
                      </div>
                      <p className="line-clamp-2 min-h-10 text-sm text-muted-foreground break-words">
                        {preview || (subCount > 0 ? "Sub-topics have no names yet" : "No answer yet")}
                      </p>
                    </li>
                  );
                })}
              </ul>
            )}

            {(visibleCount < filteredTopics.length || visibleCount > 5) && (
              <div className="flex justify-center">
                {visibleCount < filteredTopics.length ? (
                  <Button type="button" variant="outline" size="sm" onClick={showMoreMenus}>
                    Show more topics
                  </Button>
                ) : (
                  <Button type="button" variant="outline" size="sm" onClick={() => setVisibleCount(5)}>
                    Show fewer topics
                  </Button>
                )}
              </div>
            )}
          </SectionCard>
        </section>

        <OrderManageModal
          show={showOrderModal}
          onHide={closeOrderModal}
          labelQuestions={orderDraft}
          handleDragEnd={handleDragEnd}
          saveSortedOrder={saveSortedOrder}
          saving={orderSaving}
          changed={orderChanged(orderDraft, labelQuestions)}
        />
      </div>
      </div>
      <DeleteModal
        show={confirmModal.show}
        label={confirmModal.label}
        onClose={() => setConfirmModal({ ...confirmModal, show: false })}
        onConfirm={confirmModal.onConfirm}
      />
      <Modal show={!!viewTopic} onHide={closeTopicView} centered size="lg" enforceFocus={false} aria-labelledby="topicViewTitle">
        {viewTopic && (() => {
          const lq = viewTopic;
          const displayIndex = labelQuestions.indexOf(lq) + 1;
          const subCount = lq.subQuestions?.length || 0;
          return (
            <>
              <ModalHeader
                icon={<ListTree />}
                title={<span id="topicViewTitle">{lq.mainQuestion}</span>}
                description={subCount > 0 ? `${subCount} sub-topic${subCount === 1 ? '' : 's'}` : "Direct answer"}
                onClose={closeTopicView}
              />
              <Modal.Body>
                <div data-evo-island className="text-foreground">
                  {subCount > 0 ? (
                    <ul className="flex flex-col" aria-label={`Sub-topics of ${lq.mainQuestion}`}
                      onClickCapture={(e) => {
                        if ((e.target as HTMLElement).closest("button")) closeTopicView();
                      }}>
                      {renderSubQuestions(
                              lq.subQuestions,
                              lq.id,
                              updateSubQuestion,
                              handleSubMenuDelete,
                              addNestedSubQuestion,
                              2,
                              `${displayIndex}`,
                              setConfirmModal,
                              setSubMenuContext,
                              setShowSubMenuModal,
                              labelQuestions,
                              setSubmenuBackup
                            )}
                    </ul>
                  ) : htmlToText(lq.mainAnswer) ? (
                    <div className={richTextClass} dangerouslySetInnerHTML={{ __html: sanitizeHtml(lq.mainAnswer) }} />
                  ) : (
                    <p className="text-sm text-muted-foreground">No answer yet</p>
                  )}
                </div>
              </Modal.Body>
              <ModalFooter>
                <Button type="button" variant="outline"
                  className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                  onClick={() => {
                    closeTopicView();
                    setConfirmModal({
                      show: true,
                      label: `Delete topic : "${lq.mainQuestion}"`,
                      onConfirm: () => handleDeleteConfirmed(lq.id),
                    });
                  }}>
                  <Trash2 aria-hidden="true" />Delete
                </Button>
                <Button type="button" variant="outline" onClick={() => {
                  closeTopicView();
                  (() => {
                              const newSub = {
                                id: Date.now(),
                                question: '',
                                answer: '',
                                answerType: 'editor',
                                subQuestions: [],
                              };

                              // Inject new sub into parent
                              setLabelQuestions(prev =>
                                prev.map(q => {
                                  if (q.id === lq.id) {
                                    return { ...q, subQuestions: [...q.subQuestions, newSub] };
                                  }
                                  return q;
                                })
                              );

                              setSubMenuContext({
                                parentId: lq.id,
                                subId: newSub.id,
                                isEdit: false
                              });

                              setShowSubMenuModal(true);
                            })();
                }}>
                  <Plus aria-hidden="true" />Add sub-topic
                </Button>
                <Button type="button" onClick={() => {
                  closeTopicView();
                  handleMenuEdit(lq.intentID);
                }}>
                  <Pencil aria-hidden="true" />Edit
                </Button>
              </ModalFooter>
            </>
          );
        })()}
      </Modal>
      <Modal
        show={showAddModal}
        backdrop="static"
        keyboard={false}
        enforceFocus={false}
        onHide={requestMenuClose}
        centered
        size="lg"
        aria-labelledby="topicModalTitle"
      >
        <ModalHeader
          icon={<ListTree />}
          title={<span id="topicModalTitle">{editingMenuId ? "Edit topic" : "Add topic"}</span>}
          description={menuForm.title?.trim() || (editingMenuId ? "Unnamed Topic" : "New Topic")}
          onClose={requestMenuClose}
        />

        <Modal.Body>
          {
            modalLoading && (
              <>
                <div className="loaderOverlaySection zIndex-1023"><div className="loading"><div className="main-loader"></div></div></div>
              </>
            )

          }
          <div className={modalLoading ? "blur-sm pointer-events-none" : ""}>
            <div className="w-100">
              <div data-evo-island className="mb-4 grid gap-4 text-foreground sm:grid-cols-[minmax(0,1fr)_14rem]">
                <Field label="Topic name" htmlFor="topicName" required error={formErrors.title}>
                  <Input
                    id="topicName"
                    placeholder="Topic Name"
                    value={menuForm.title}
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        // Check if invalid characters are being entered
                        if (inputValue.includes('/') || inputValue.includes('\\')) {
                          setInvalidCharMessage({
                            show: true,
                            message: "Characters '/' and '\\' are not allowed in topic name"
                          });
                          // Hide message after 5 seconds
                          setTimeout(() => {
                            setInvalidCharMessage({ show: false, message: "" });
                          }, 5000);
                        }
                        const sanitizedValue = sanitizeTitle(inputValue);
                        // Only set isDirty if the value actually changed
                        if (sanitizedValue !== (menuForm.title || '')) {
                          setIsDirty(true);
                        }
                        setMenuForm(prev => ({ ...prev, title: sanitizedValue }))
                        if (formErrors.title) {
                          setFormErrors(prev => ({ ...prev, title: "" }));
                        }
                      }}
                  />
                  {invalidCharMessage.show && (
                    <p className="flex items-center gap-1 text-sm text-warning-foreground" role="status">
                      <TriangleAlert className="size-4" aria-hidden="true" />
                      {invalidCharMessage.message}
                    </p>
                  )}
                </Field>
                <Field label="Answer type" htmlFor="topicAnswerType">
                  <NativeSelect
                    id="topicAnswerType"
                    value={menuForm.answerType}
                    onChange={(e) => {
                      setMenuForm(prev => ({ ...prev, answerType: e.target.value }))
                      setIsDirty(true)
                    }}
                  >
                    <option value="text">Text</option>
                    <option value="editor">Rich Text Editor</option>
                  </NativeSelect>
                </Field>
              </div>
              <div className="mb-3 w-100 mt-2">
                {menuForm.answerType === 'text' ? (
                  <>
                    <p data-evo-island className="mb-1.5 text-sm font-medium text-foreground">Bot response</p>
                    <input
                      className="form-control"
                      placeholder="Enter answer"
                      value={menuForm.answer}
                      onChange={(e) => {
                        setMenuForm(prev => ({ ...prev, answer: e.target.value }))
                        setIsDirty(true)
                      }
                      }
                    />
                  </>
                ) : (
                  <>
                    <p data-evo-island className="mb-1.5 text-sm font-medium text-foreground">Bot response</p>
                    <div className="custom-editor-wrapper">
                      <CKEditor
                        editor={ClassicEditorBase}
                        data={sanitizeHtml(menuForm.answer || '')}
                        onReady={sanitizeCKEditorOnReady}
                        onChange={(event, editor) => {
                          setMenuForm(prev => ({ ...prev, answer: editor.getData() }))
                          setIsDirty(true)
                        }
                        }
                        config={{
                          toolbar: [
                            'heading', '|', 'bold', 'italic', 'link', 'bulletedList',
                            'numberedList', 'blockQuote', 'insertTable', 'undo', 'redo'
                          ],
                          link: {
                            decorators: {
                              openInNewTab: {
                                mode: 'automatic',
                                callback: () => true,
                                attributes: {
                                  target: '_blank',
                                  rel: 'noopener noreferrer',
                                },
                              },
                            },
                          },
                        }}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>


        </Modal.Body>

        <ModalFooter>
          <Button type="button" variant="outline" onClick={requestMenuClose}>
            Close
          </Button>
          <Button type="button" onClick={saveSingleMenu}>
            Save
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        backdrop="static"
        keyboard={false}
        enforceFocus={false}
        show={showSubMenuModal}
        //onHide={() => setShowSubMenuModal(false)}
        onHide={clossubmenu}
        centered
        size="lg"
        aria-labelledby="subTopicModalTitle"

      >
        <ModalHeader
          icon={<CornerDownRight />}
          title={<span id="subTopicModalTitle">{subMenuContext.isEdit ? "Edit sub-topic" : "Add sub-topic"}</span>}
          description={(() => {
            const parent = labelQuestions.find(q => q.id === subMenuContext.parentId);
            return parent
              ? findIntentPathBySubId([parent], subMenuContext.subId)
                ?.map(q => q.question || q.mainQuestion)
                .join(" / ")
              : "Sub-Topic";
          })()}
          onClose={clossubmenu}
        />
        <Modal.Body>
          {
            modalLoading && (
              <div className="loaderOverlaySection zIndex-1023"><div className="loading"><div className="main-loader"></div></div></div>
            )
          }
          {(() => {
            const parent = labelQuestions.find(q => q.id === subMenuContext.parentId);

            if (!parent) return;

            let sub;

            if (subMenuContext.subId) {
              sub = findSubQuestionById(parent.subQuestions, subMenuContext.subId);

              if (!sub) {
                return <div className="text-danger">Subtopic not found or already removed.</div>;
              }

              // ✅ Store original label if not already stored
              if (!sub.originalLabel) {
                sub.originalLabel = sub.question;
              }

            } else {
              sub = {
                id: Date.now(),
                question: '',
                answer: '',
                answerType: 'text',
                subQuestions: [],
              };
            }


            return (
              <>
                <div className={modalLoading ? "blur-sm pointer-events-none" : ""}>
                  <div className="d-flex gap-3 mb-2">
                    <div className="input-group">
                      <span className="contactRightIo"><strong>*</strong></span>
                      <input
                        className="form-control no-left-padding"
                        placeholder="Sub-topic Question"
                        value={sub.question}
                        onChange={(e) => {
                          const inputValue = e.target.value;
                          // Check if invalid characters are being entered
                          if (inputValue.includes('/') || inputValue.includes('\\')) {
                            setInvalidCharMessage({
                              show: true,
                              message: "Characters '/' and '\\' are not allowed in sub-topic name"
                            });
                            // Hide message after 5 seconds
                            setTimeout(() => {
                              setInvalidCharMessage({ show: false, message: "" });
                            }, 5000);
                          }
                          const sanitizedValue = sanitizeTitle(inputValue);
                          // Only set isDirty if the value actually changed
                          if (sanitizedValue !== (sub.question || '')) {
                            setIsDirty(true);
                          }
                          updateSubQuestion(subMenuContext.parentId, sub.id, 'question', sanitizedValue)
                          if (formErrors.title) {
                            setFormErrors(prev => ({ ...prev, title: "" }));
                          }
                        }}
                      />
                    </div>

                    <select
                      className="form-select"
                      value={sub.answerType}
                      onChange={(e) => {
                        updateSubQuestion(subMenuContext.parentId, sub.id, 'answerType', e.target.value)
                        setIsDirty(true)
                        
                      }
                      }
                    >
                      <option value="text">Text</option>
                      <option value="editor">Rich Text Editor</option>
                    </select>
                  </div>
                  {formErrors.title && (
                    <div className="text-danger mt-1" style={{ fontSize: "14px" }}>
                      {formErrors.title}
                    </div>
                  )}
                  {invalidCharMessage.show && (
                    <div className="text-warning mt-1" style={{ fontSize: "14px" }}>
                      <i className="bi bi-exclamation-triangle me-1"></i>
                      {invalidCharMessage.message}
                    </div>
                  )}
                  <div className="mb-3 mt-2">
                    {sub.answerType === 'text' ? (
                      <input

                        className="form-control"
                        value={sub.answer}
                        onChange={(e) => {
                          updateSubQuestion(subMenuContext.parentId, sub.id, 'answer', e.target.value)
                          setIsDirty(true)
                        }
                        }
                      />
                    ) : (
                      <>
                        <label className="form-label">Answer</label>
                        <div className="custom-editor-wrapper">
                          <CKEditor
                            editor={ClassicEditorBase}
                            data={sanitizeHtml(sub.answer || '')}
                            onReady={sanitizeCKEditorOnReady}
                            onChange={(event, editor) => {
                              updateSubQuestion(
                                subMenuContext.parentId,
                                sub.id,
                                'answer',
                                editor.getData()
                              )
                              setIsDirty(true)
                            }
                            }
                            config={{
                              toolbar: [
                                'heading',
                                '|',
                                'bold',
                                'italic',
                                'link',
                                'bulletedList',
                                'numberedList',
                                'blockQuote',
                                'insertTable',
                                'undo',
                                'redo',
                              ],
                              link: {
                                decorators: {
                                  openInNewTab: {
                                    mode: 'automatic',
                                    callback: () => true,
                                    attributes: {
                                      target: '_blank',
                                      rel: 'noopener noreferrer',
                                    },
                                  },
                                },
                              },
                            }}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>

              </>
            );
          })()}
        </Modal.Body>
        <ModalFooter>
          <Button type="button" variant="outline" onClick={clossubmenu}>
            Close
          </Button>
          <Button type="button" onClick={subMenuContext.isEdit ? handleSubMenuUpdate : handleSubMenuAdd}>
            Save
          </Button>
        </ModalFooter>
      </Modal>



    </>
  );
};

export default IntentSection;

